import { Inject } from '@odinjs/odin';
import { ActionAPI, AutocompleteAPI, ButtonAPI, CheckboxAPI, ContextualBarAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, ModalService, RadioGroupAPI, SelectableItem } from '@philips-internal/oswald-ui-components';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants/lib';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnCancel, OnChange, OnClick, OnEdit, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ContextType, SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { ChangeEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { HttpServiceError } from 'src/services/patient-http-service';
import { FragmentNames, MyChartMedicinePatientAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import Medicine from '../../interfaces/medicine';
import DocumentContext from '../../utils/document-context';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';

enum MedicineSequence {
  NEW = 'new',
  TYPE_ACTION = 'new_medicament'
}

enum ContinuousMedication {
  CONFIRM = 'C',
  DENY = 'N',
  UNAWARE = 'D'
}

export enum InformDose {
  NOT_INFORMED_DOSE = 'S',
  INFORMED_DOSE = 'N'
}

interface MedicineFormRecord extends Record<string, Value> {
  continuousMedication: Situation;
  dontKnowTheDose: InformDose;
  dose: string;
  endDate: Date;
  inactivationDate: Date;
  interval: string;
  medicine: MaterialForm;
  notes: string;
  releaseDate: Date;
  sequence: number;
  startDate: Date;
  unitMeasurement: UnitMeasurementForm;
  unregisteredMedicine: string;
}

interface MaterialForm {
  material: string;
  sequence: string;
}

interface UnitMeasurementForm {
  unitMeasurement: string;
  unitMeasurementCode: string;
}

interface Situation {
  key: string;
  label: string;
  value: string;
}

const CONTINUOS_MEDICATION_VALUES: SelectableItem[] = [
  {key: ContinuousMedication.CONFIRM, label: MessageService.translate('patient.radio.confirm'), value: ContinuousMedication.CONFIRM},
  {key: ContinuousMedication.DENY, label:MessageService.translate('patient.radio.deny'), value: ContinuousMedication.DENY},
  {key: ContinuousMedication.UNAWARE, label:MessageService.translate('patient.radio.unaware'), value: ContinuousMedication.UNAWARE}
];

@Controller({ fragment: FragmentNames.MY_CHART_MEDICINE_PATIENT })
class MyChartMedicinePatientController extends BaseController {

  @Inject(MyChartMedicinePatientAliases.FORM_MEDICINE_PATIENT)
    medicinePatientFormApi!: FormAPI;

  @Inject(MyChartMedicinePatientAliases.INPUT_UNREGISTERED_MEDICINE)
    unregisteredMedicineInputAPI!: InputAPI;

  @Inject(MyChartMedicinePatientAliases.AUTOCOMPLETE_MATERIAL)
    materialAutoCompleteAPI!: AutocompleteAPI;

  @Inject(MyChartMedicinePatientAliases.AUTOCOMPLETE_UNIT_MEASUREMENT)
    unitMeasurementAutoCompleteAPI!: AutocompleteAPI;

  @Inject(MyChartMedicinePatientAliases.FETCH_UNIT_MEASUREMENT)
    unitMeasurementFetchAPI!: FetchAPI;

  @Inject(MyChartMedicinePatientAliases.INPUT_DOSE)
    doseInputAPI!: InputAPI;

  @Inject(MyChartMedicinePatientAliases.FIELD_UNREGISTERED_MEDICINE)
    medicineUnregisteredFieldApi!: FieldAPI;

  @Inject(MyChartMedicinePatientAliases.INPUT_INTERVAL)
    intervalInputApi!: InputAPI;

  @Inject(MyChartMedicinePatientAliases.CHECKBOX_DONT_KNOW_DOSE)
    dontKnowDoseCheckboxApi!: CheckboxAPI

  @Inject(MyChartMedicinePatientAliases.ACTION_MEDICINE_INACTIVATE)
    inactivateActionApi!: ActionAPI;

  @Inject(MyChartMedicinePatientAliases.ACTION_MEDICINE_RELEASE)
    releaseActionApi!: ActionAPI;

  @Inject(MyChartMedicinePatientAliases.ACTION_MEDICINE_DELETE)
    deleteActionApi!: ActionAPI;

  @Inject(MyChartMedicinePatientAliases.BTN_SEND_DOCUMENT)
    sendDocumentBtnApi!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount(MyChartMedicinePatientAliases.CONTEXTUAL_BAR_MEDICINE_PATIENT)
  handleMount(event: MountEvent): void {
    const contextualBarApi = event.target as ContextualBarAPI;
    const typeAction = this.getHistoryRouteParam<string>('typeAction');
    if (typeAction !== 'new_medicament') {
      this.handleWhenIsDetailRoute(this.medicinePatientFormApi, contextualBarApi);
    }
  }

  private handleWhenIsDetailRoute(medicinePatientFormApi: FormAPI, contextualBarApi: ContextualBarAPI): void {
    this.setContextualBarTitle(contextualBarApi);
    this.medicinePatientFormApi.setLoading(true);
    this.getFormDataWhenIsDetailRoute(medicinePatientFormApi);
  }

  private setContextualBarTitle(contextualBarApi: ContextualBarAPI) : void{
    const title = `${MessageService.translate('patient.label.medicine-detail')} - ${MessageService.translate('patient.my-chart.label.patient')}`;
    contextualBarApi.setTitle(title);
  }

  private getFormDataWhenIsDetailRoute(medicinePatientFormApi: FormAPI): void {
    medicinePatientFormApi.setRecord({});
    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_MEDICINE_PATIENT_DETAIL, { medicamentSequence: this.getMedicineSequence(), patientId: UserContext.patientId })
      .then(response => response.body as Record<string, Value>)
      .then(data => {
        data.startDateDetail = data.startDate ? moment(data.startDate as string).format('L') : '';
        data.endDateDetail = data.endDate ? moment(data.endDate as string).format('L') : '';
        data.startDate = data.startDate ? moment(data.startDate as string).format(ISO_DATE_FORMAT) : '';
        data.endDate = data.endDate ? moment(data.endDate as string).format(ISO_DATE_FORMAT) : '';
        data.releaseDate = data.releaseDate ? moment(data.releaseDate as string).format(ISO_DATE_FORMAT) : '';
        data.inactivationDate = data.inactivationDate ? moment(data.inactivationDate as string).format(ISO_DATE_FORMAT) : '';
        data.intervalDetail = data.interval?.toString() || '-';
        data.notes = data.notes?.toString() || '-';
        data.doseDetail = data.dose || '-';
        data.dontKnowTheDose = data.dontKnowTheDose?.toString() === InformDose.NOT_INFORMED_DOSE
        data.dontKnowTheDoseDetail = data.dontKnowTheDose ? MessageService.translate('patient.moda.button-yes') : MessageService.translate('patient.modal.button-cancel');
        data.unitMeasurementDetail = data.unitMeasurement?.toString() || '-';
        data.unitMeasurement = {unitMeasurement: data.unitMeasurement, unitMeasurementCode: data.unitMeasurementCode};

        if (data.materialCode != null ) {
          data.medicine = {material : data.medicament, sequence : data.materialCode};
          data.medicineDetail = data.medicament?.toString() || '-';
          data.unregisteredMedicineDetail = '-';
        } else {
          data.medicineDetail = '-';
          data.unregisteredMedicine = data.medicament?.toString();
          data.unregisteredMedicineDetail = data.medicament?.toString() || '-';
        }

        switch (data.continuousMedication?.toString()) {
          case ContinuousMedication.CONFIRM:
            data.continuousMedication = CONTINUOS_MEDICATION_VALUES[0];
            data.continuousMedicationDetail = MessageService.translate('patient.radio.confirm');
            break;
          case ContinuousMedication.DENY:
            data.continuousMedication = CONTINUOS_MEDICATION_VALUES[1];
            data.continuousMedicationDetail = MessageService.translate('patient.radio.deny');
            break;
          case ContinuousMedication.UNAWARE:
            data.continuousMedication = CONTINUOS_MEDICATION_VALUES[2];
            data.continuousMedicationDetail = MessageService.translate('patient.radio.unaware');
            break;
          default:
            data.continuousMedication = CONTINUOS_MEDICATION_VALUES[0];
            data.continuousMedicationDetail = '-';
            break;
        }

        this.medicinePatientFormApi.setData(data);
        this.medicinePatientFormApi.setEditing(false);
        this.medicinePatientFormApi.setLoading(false);
        this.updateStateFormActions(false);
      });
  }

  private updateStateFormActions(isFormEditing: boolean): void {
    const medicamentSequence = this.getMedicineSequence();

    if(medicamentSequence === MedicineSequence.NEW){
      return;
    }

    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_MEDICINE_PATIENT_DETAIL, { medicamentSequence: medicamentSequence, patientId: UserContext.patientId })
      .then(response => response.body as Record<string, Value>)
      .then(response => {
        const hasReleaseDate = response.releaseDate != null;
        const hasinactivationDate = response.inactivationDate != null;
        this.setStateFormActions(isFormEditing, hasReleaseDate, hasinactivationDate);
      })
      .catch(({response}: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private setStateFormActions(isFormEditing: boolean, hasReleaseDate: boolean, hasinactivationDate: boolean): void {
    this.medicinePatientFormApi.setEditing(isFormEditing);
    this.medicinePatientFormApi.setDisabled(hasReleaseDate || hasinactivationDate);
    this.inactivateActionApi.setEnabled(!isFormEditing && hasReleaseDate && !hasinactivationDate);
    this.releaseActionApi.setEnabled(!isFormEditing && !hasReleaseDate && !hasinactivationDate);
    this.deleteActionApi.setEnabled(!isFormEditing && !hasReleaseDate && !hasinactivationDate);
    this.sendDocumentBtnApi.setVisible(false);
  }

  @OnEdit({ target: MyChartMedicinePatientAliases.FORM_MEDICINE_PATIENT})
  handleEditForm() : void{
    this.updateStateFormActions(true);
    this.sendDocumentBtnApi.setVisible(false);

    this.materialAutoCompleteAPI.onChange.fire(new ChangeEvent({value: this.materialAutoCompleteAPI.getValue()}));
    this.unregisteredMedicineInputAPI.onChange.fire(new ChangeEvent({value: this.unregisteredMedicineInputAPI.getValue()}));
    this.dontKnowDoseCheckboxApi.onChange.fire(new ChangeEvent({value: this.dontKnowDoseCheckboxApi.getValue()}));
  }

  @OnClick({ target: MyChartMedicinePatientAliases.ACTION_MEDICINE_INACTIVATE })
  handleBtnInactivateMedicine(): void {
    ModalService.openDialog({
      closeable: true,
      fragment: FragmentNames.MY_CHART_MEDICINE_INACTIVATE,
      onSubmit: () => {
        this.handleRedirectMedicine();
      },
      title: 'patient.label.justification'
    });
  }

  @OnClick({ target: MyChartMedicinePatientAliases.ACTION_MEDICINE_RELEASE })
  handleBtnReleaseMedicine(): void {
    const message = 'patient.my-chart.modal.want-release';
    this.handleReleaseConfirmationModal(message, this.getMedicineSequence());
  }

  @OnClick({ target: MyChartMedicinePatientAliases.ACTION_MEDICINE_DELETE })
  handleBtnDeleteMedicine(): void {
    const medicamentSequence = this.getMedicineSequence();

    ModalService.openDialog({
      closeable: true,
      dialogDescription: 'patient.my-chart.delete-item',
      dockButtons: [
        {
          action: (): void => { close() },
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => {
            this.deleteMedicament(medicamentSequence);
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }]
    });
  }

  @OnSubmit({ target: MyChartMedicinePatientAliases.FORM_MEDICINE_PATIENT})
  handleSubmit(event: SubmitEvent): void {
    const message = 'patient.my-chart.modal.wish-submit';

    const medicineForm = event.record as MedicineFormRecord;
    const medicineSequence = this.getMedicineSequence();
    const sequence = medicineSequence === MedicineSequence.NEW ?  '' : medicineSequence;
    const startDate = medicineForm.startDate ? moment(medicineForm.startDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT) : '';
    const endDate = medicineForm.endDate ? moment(medicineForm.endDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT) : '';
    const { documentIds } = DocumentContext;

    const body = {
      continuousMedication: medicineForm.continuousMedication?.value,
      documentIds: documentIds,
      dontKnowTheDose: medicineForm.dontKnowTheDose ? InformDose.NOT_INFORMED_DOSE : InformDose.INFORMED_DOSE,
      dose: medicineForm.dose,
      endDate: endDate,
      id: sequence,
      interval: medicineForm.interval,
      materialCode: medicineForm.medicine?.sequence,
      medicament: medicineForm.medicine?.material || medicineForm.unregisteredMedicine,
      notes: medicineForm.notes,
      startDate: startDate,
      unitMeasurement: medicineForm.unitMeasurement?.unitMeasurementCode
    } as Record<string, Value>;

    this.context.set({
      params: {
        ...body,
        ...medicineForm,
      }
    });

    HttpService.put(APPLICATION_ENDPOINTS.POST_MC_MEDICINE, { patientId: UserContext.patientId }, { body })
      .then(response => response.body as Record<string, Value>)
      .then(response => {
        this.handleReleaseConfirmationModal(message, response.sequence as number);
      })
      .catch(({response}: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private handleReleaseConfirmationModal(message: string, medicineId: Value): void {
    this.handleRedirectMedicine();
    ModalService.openDialog({
      closeable: true,
      dialogDescription: message,
      dockButtons: [
        {
          action: (): void => { close() },
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => {
            this.releaseMedicine(medicineId);
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ]
    });
  }

  @OnCancel({target: MyChartMedicinePatientAliases.FORM_MEDICINE_PATIENT})
  handleCancelButton(): void {
    const typeAction = this.getHistoryRouteParam<string>('typeAction');
    if (typeAction === MedicineSequence.TYPE_ACTION) {
      this.handleRedirectMedicine();
    }
    this.setStateFormActions(false, false, false);
  }

  @OnMount({target:MyChartMedicinePatientAliases.RADIO_CONTINUOUS_MEDICATION})
  onMountRadioGroupContinuousMedication( evt: MountEvent ): void {
    const radioContinuousMedication = evt.target as RadioGroupAPI;
    radioContinuousMedication.setData(CONTINUOS_MEDICATION_VALUES);
  }

  @OnChange({ target: MyChartMedicinePatientAliases.FIELD_UNREGISTERED_MEDICINE })
  onChangeUnregisteredMedicine(event: ChangeEvent<Medicine>): void {
    this.materialAutoCompleteAPI.setEnabled(!event.value);
    this.updateUnitMeasurement(event);
  }

  @OnChange({ target: MyChartMedicinePatientAliases.AUTOCOMPLETE_MATERIAL })
  onChangeAutoCompleteMaterial(event: ChangeEvent<Medicine>): void {
    this.unregisteredMedicineInputAPI.setEnabled(!event.value);
    this.updateUnitMeasurement(event);
  }

  private updateUnitMeasurement(event: ChangeEvent<Medicine>): void {
    const isHasValue = Boolean(event.value || this.materialAutoCompleteAPI.getValue() || this.unregisteredMedicineInputAPI.getValue());
    this.unitMeasurementAutoCompleteAPI.setEnabled(isHasValue && !this.dontKnowDoseCheckboxApi.getValue());

    const params = {
      medicine: event.value.medicine,
      sequence: event.value.sequence
    }

    this.unitMeasurementFetchAPI.setParameters(params);
    this.unitMeasurementFetchAPI.forceFetch();
  }

  @OnChange({ target: MyChartMedicinePatientAliases.CHECKBOX_DONT_KNOW_DOSE })
  onChangeDontKnowDoseCheckbox(event: ChangeEvent<boolean>): void {
    const checked = Boolean(event.value);
    this.doseInputAPI.setEnabled(!checked);
    this.unitMeasurementAutoCompleteAPI.setEnabled(!checked);
    this.intervalInputApi.setEnabled(!checked);

    if (checked) {
      this.doseInputAPI.setValue('');
      this.unitMeasurementAutoCompleteAPI.setValue({unitMeasurement: null, unitMeasurementCode: null});
      this.intervalInputApi.setValue('');
    }
  }

  openReport = (path: string): void => {
    ModalService.openDialog({
      className: 'my-chart-sending-documents__dialog-modal',
      contextData: { filePath: path },
      fragment: 'my-chart-view-sent-document'
    });
  }

  @OnClick({ target: MyChartMedicinePatientAliases.BTN_SEND_DOCUMENT })
  handleBtnSendNewDocument(): void {
    ModalService.openFullscreen({
      closeable: true,
      fragment: FragmentNames.MY_CHART_DOCUMENT,
      title: 'patient.label.new-document'
    })
  }

  private releaseMedicine(medicineId: Value): void {
    const body = {
      id: medicineId
    } as Record<string, Value>;

    HttpService.post(APPLICATION_ENDPOINTS.POST_MC_MEDICINE_RELEASE, {  patientId: UserContext.patientId }, { body })
      .then(() => showToastNotificationSuccess('patient.my-chart.released-success'))
      .catch(({response}: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private deleteMedicament = (medicamentSequence: string): void => {
    HttpService.delete(APPLICATION_ENDPOINTS.DELETE_MC_MEDICINE, { medicamentSequence })
      .then(() => {
        showToastNotificationSuccess('patient.my-chart.delete-success');
        this.handleRedirectMedicine();
      })
      .catch(({response}: HttpServiceError<string>) => {
        this.showErrorNotification(response?.body as string);
      });
  }

  private showErrorNotification(messageServer?: string):void {
    const message = messageServer || MessageService.translate('newaccount.error.internal.server.error.try.again');
    showToastNotificationError(message);
  }

  private getMedicineSequence(): string {
    return this.getHistoryRouteParam<string>('medicineSequence');
  }

  private handleRedirectMedicine() : void {
    applicationContext.popNavigationItem();
  }
}

export default MyChartMedicinePatientController;
